import type { ReactNode } from 'react';
import { SparkIcon } from '../SparkIcon';
import { Checkbox } from '../Checkbox/Checkbox';
import { ReadOnlyTooltip, useReadOnlyPause } from '../ReadOnly/ReadOnly';
import { DropdownCheckIcon } from './DropdownCheckIcon';
import './Dropdown.css';

export type DropdownItemState = 'default' | 'hover' | 'selected' | 'disabled';

/** Single row inside a Dropdown list. Renders as a regular item, checkbox item, or cascading item. */
export type DropdownItemTone = 'default' | 'danger';

export type DropdownItemProps = {
  label?: ReactNode;
  /**
   * Plain-text value used by `DropdownSearch` to filter this row when `label`
   * is a React node (e.g. a `TagColored` chip).
   */
  searchText?: string;
  /** Optional leading node before the label text (e.g. a status color dot on checkbox rows). */
  prefix?: ReactNode;
  /** Icon slug for `showPrefixIcon` (from the Spark icon set). Defaults to "user". */
  prefixIconSlug?: string;
  /** Danger rows (e.g. Delete) use the action-danger content color. */
  tone?: DropdownItemTone;
  /**
   * Optional trailing node after the label/check (e.g. a row action icon button).
   * Click handlers on the suffix should call `stopPropagation` so the row is not selected.
   */
  suffix?: ReactNode;
  state?: DropdownItemState;
  showPrefixIcon?: boolean;
  showInfoIcon?: boolean;
  checked?: boolean;
  /**
   * Storybook-only visual state override for static matrices.
   */
  visualState?: DropdownItemState;
  /** `'section'` marks the row as a non-interactive group header (e.g. "Versions" / "Query File") — `Dropdown` renders it as a title instead of a `DropdownItem`. */
  type?: 'regular' | 'checkbox' | 'section';
  /** Shows a chevron-right icon on the right side, indicating this item expands to a sub-menu. */
  showExpandIcon?: boolean;
  /** Rotates the expand chevron downward to indicate the item is open. */
  expandIconOpen?: boolean;
  /** Fired when the expand chevron is clicked (cascading type). */
  onExpand?: () => void;
  /** Sub-items shown inline below this item when it is expanded (cascading type) or in a side submenu panel (flyout type). */
  children?: DropdownItemProps[];
  /** For `Dropdown type='flyout'`: optional section-style header (e.g. "Model version") rendered above this item's `children` in the flyout panel. */
  childrenTitle?: string;
  /** DOM id for a11y (`aria-activedescendant`). */
  id?: string;
  className?: string;
  /** Disable this item while a `ReadOnlyProvider` is read-only, with its reason as a tooltip. */
  disableWhenReadOnly?: boolean;
  onClick?: () => void;
};

export function DropdownItem({
  label = 'Dropdown item text',
  prefix,
  prefixIconSlug = 'user',
  suffix,
  state = 'default',
  tone = 'default',
  showPrefixIcon = false,
  showInfoIcon = false,
  showExpandIcon = false,
  expandIconOpen = false,
  checked = false,
  visualState,
  type = 'regular',
  id,
  className = '',
  disableWhenReadOnly,
  onClick,
  onExpand,
}: DropdownItemProps) {
  const { paused, reason } = useReadOnlyPause(disableWhenReadOnly);
  const resolvedState = paused ? 'disabled' : visualState ?? state;
  const isDisabled = resolvedState === 'disabled';
  const isSelected = resolvedState === 'selected';
  const isHover = resolvedState === 'hover';
  const isDanger = tone === 'danger';

  const rootClasses = [
    'spark-dropdown-item',
    isHover ? 'spark-dropdown-item-hover' : '',
    isSelected ? 'spark-dropdown-item-selected' : '',
    isDisabled ? 'spark-dropdown-item-disabled' : '',
    isDanger ? 'spark-dropdown-item-danger' : '',
    showExpandIcon ? 'spark-dropdown-item-cascading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const row = type === 'checkbox' ? (
      <div
        id={id}
        className={rootClasses}
        role="menuitemcheckbox"
        aria-checked={checked}
        aria-disabled={isDisabled || undefined}
        onClick={isDisabled ? undefined : onClick}
      >
        {/* Checkbox is visual/controlled; the row owns the toggle hit-target. */}
        <Checkbox
          label={typeof label === 'string' ? label : 'Dropdown item text'}
          prefix={prefix}
          status={checked ? 'active' : 'inactive'}
          disabled={isDisabled}
          visualState={isHover ? 'hover' : undefined}
          tabIndex={-1}
          className="spark-dropdown-item-checkbox"
        />
        {suffix}
        {showInfoIcon ? <SparkIcon slug="circle-info" size="small" className="spark-dropdown-item-info" /> : null}
      </div>
    ) : (
      <div
        id={id}
        className={rootClasses}
        role="menuitem"
        aria-disabled={isDisabled || undefined}
        onClick={isDisabled ? undefined : onClick}
      >
        <div className="spark-dropdown-item-content">
          {prefix ?? (showPrefixIcon ? <SparkIcon slug={prefixIconSlug} size="small" className="spark-dropdown-item-prefix" /> : null)}
          <span className="spark-dropdown-item-label">{label}</span>
          {isSelected ? <DropdownCheckIcon className="spark-dropdown-item-check" /> : null}
        </div>
        {suffix}
        {showInfoIcon ? <SparkIcon slug="circle-info" size="small" className="spark-dropdown-item-info" /> : null}
        {showExpandIcon ? (
          <button
            className={['spark-dropdown-item-expand-btn', expandIconOpen ? 'spark-dropdown-item-expand-btn-open' : ''].filter(Boolean).join(' ')}
            aria-label={expandIconOpen ? 'Collapse' : 'Expand'}
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) onExpand?.();
            }}
          >
            <SparkIcon slug="chevron-right" size="small" />
          </button>
        ) : null}
      </div>
    );

  return (
    <ReadOnlyTooltip paused={paused} reason={reason} block placement="left">
      {row}
    </ReadOnlyTooltip>
  );
}
